/* Announcement Bar Slider (externalized) */
(function () {
  function initAnnouncementSlider(root) {
    const slider = root || document.querySelector("[data-announcement-slider]");
    if (!slider) return;

    const slidesContainer = slider.querySelector("[data-slides-container]");
    const slides = slider.querySelectorAll(".an-Slide:not(.an-Slide--clone)");
    const indicators = slider.querySelectorAll("[data-slide-indicator]");
    const prevBtn = slider.querySelector("[data-nav-prev]");
    const nextBtn = slider.querySelector("[data-nav-next]");

    // Create clones for infinite loop (prepend last, append first)
    // Keep clones out of the "slides" collection by using the :not(.an-Slide--clone) selector above
    if (slides.length > 1 && slider.querySelectorAll(".an-Slide--clone").length === 0) {
      const firstClone = slides[0].cloneNode(true);
      const lastClone = slides[slides.length - 1].cloneNode(true);
      firstClone.classList.add("an-Slide--clone");
      lastClone.classList.add("an-Slide--clone");
      // avoid duplicate IDs and remove any aria-live on clones to avoid duplicate announcements
      firstClone.removeAttribute("id");
      lastClone.removeAttribute("id");
      firstClone.querySelectorAll("[aria-live]").forEach((el) => el.removeAttribute("aria-live"));
      lastClone.querySelectorAll("[aria-live]").forEach((el) => el.removeAttribute("aria-live"));
      // make clones inert for accessibility/tabbing
      [firstClone, lastClone].forEach((c) => {
        c.setAttribute("aria-hidden", "true");
        c.setAttribute("tabindex", "-1");
      });
      // insert: [lastClone][...original slides][firstClone]
      slidesContainer.insertBefore(lastClone, slidesContainer.firstChild);
      slidesContainer.appendChild(firstClone);

      // performance hint for transforms
      slidesContainer.style.willChange = "transform";
      // ensure initial transform is applied on next frame so layout is stable
      requestAnimationFrame(() => {
        slidesContainer.style.transition = "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)";
        slidesContainer.style.transform = "translateX(-100%)";
      });
    }

    const sliderElement = slider.querySelector(".an-Slider");
    const autoPlay = sliderElement.dataset.autoPlay === "true";
    const autoplayDelayMs =
      parseInt(sliderElement.dataset.autoplayDelayMs, 10) || 4000;
    const showControls = sliderElement.dataset.showControls === "true";

    const totalSlides = slides.length;
    let current = 0;
    let actualPosition = 1; // start on first original (after clone)
    // use a single timeout for per-slide autoplay scheduling
    let autoplayTimeout = null;
    let isTransitioning = false;
    // single list of countdown descriptors (elements + end timestamp)
    let countdowns = [];
    let countdownInterval = null;
    let userInteractionDelay = null;
    const INTERACTION_DELAY_MS = 10000; // 10 seconds delay
    let navigationQueue = [];
    let isProcessingQueue = false;
    let announcementElement = null;
    // default global autoplay delay read from sliderElement (ms)
    const defaultAutoplayDelay = autoplayDelayMs;

    function initializeCountdownTimers() {
      // Build a single list of countdowns (avoid creating many intervals)
      countdowns = [];
      slides.forEach((slide, idx) => {
        if (!slide.classList.contains("an-Slide--countdown")) return;
        const endRaw = slide.dataset.countdownEnd;
        const autoHide = slide.dataset.autoHide === "true";
        if (!endRaw) return;

        // robust parse: try ISO first, fallback to replace dashes
        let endTs = Date.parse(endRaw);
        if (isNaN(endTs)) {
          endTs = Date.parse(endRaw.replace(/-/g, "/"));
        }
        if (isNaN(endTs)) return;

        const daysEl = slide.querySelector("[data-days]");
        const hoursEl = slide.querySelector("[data-hours]");
        const minutesEl = slide.querySelector("[data-minutes]");
        const secondsEl = slide.querySelector("[data-seconds]");

        countdowns.push({
          idx,
          slide,
          endTs,
          autoHide,
          daysEl,
          hoursEl,
          minutesEl,
          secondsEl,
        });
      });

      // Update function uses exact time differences so it's robust to throttling
      function updateAllCountdowns() {
        const now = Date.now();
        // iterate backwards so we can remove finished ones easily
        for (let i = countdowns.length - 1; i >= 0; i--) {
          const c = countdowns[i];
          let diff = c.endTs - now;
          if (diff <= 0) {
            if (c.daysEl) c.daysEl.textContent = "00";
            if (c.hoursEl) c.hoursEl.textContent = "00";
            if (c.minutesEl) c.minutesEl.textContent = "00";
            if (c.secondsEl) c.secondsEl.textContent = "00";
            // auto-hide when countdown ends and slide currently visible
            if (c.autoHide && current === c.idx) {
              // use autoplay-style navigation (not user interaction)
              nextSlide(false);
              // Ensure controls remain visible if there are still multiple slides
              if (slides.length > 1) {
                const controlsContainer = slider.querySelector(".an-Controls");
                if (controlsContainer) {
                  controlsContainer.style.display = "";
                }
              }
            }
            // remove from active countdowns
            countdowns.splice(i, 1);
            continue;
          }

          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
          const minutes = Math.floor((diff / (1000 * 60)) % 60);
          const seconds = Math.floor((diff / 1000) % 60);

          if (c.daysEl) c.daysEl.textContent = String(days).padStart(2, "0");
          if (c.hoursEl) c.hoursEl.textContent = String(hours).padStart(2, "0");
          if (c.minutesEl) c.minutesEl.textContent = String(minutes).padStart(2, "0");
          if (c.secondsEl) c.secondsEl.textContent = String(seconds).padStart(2, "0");
        }
      }

      // run one immediate update and then every second; also update on visibilitychange
      updateAllCountdowns();
      if (countdownInterval) clearInterval(countdownInterval);
      countdownInterval = setInterval(updateAllCountdowns, 1000);
      document.addEventListener("visibilitychange", function () {
        // immediate catch-up when tab becomes visible
        if (document.visibilityState === "visible") updateAllCountdowns();
      });
    }

    function updateArrowColors() {
      if (!showControls) return;
      const currentSlide = slides[current];
      const arrowColor = currentSlide?.dataset.arrowColor || "#ffffff";
      if (prevBtn) prevBtn.style.color = arrowColor;
      if (nextBtn) nextBtn.style.color = arrowColor;
    }

    function announceSlideChange(slideIndex, totalSlides, isAutoplay = false) {
      // Create or update announcement element for screen readers
      if (!announcementElement) {
        announcementElement = document.createElement("div");
        announcementElement.setAttribute("role", "status");
        announcementElement.setAttribute("aria-live", "polite");
        announcementElement.setAttribute("aria-atomic", "true");
        announcementElement.className = "sr-only";
        announcementElement.style.position = "absolute";
        announcementElement.style.left = "-10000px";
        document.body.appendChild(announcementElement);
      }

      const slideType = slides[slideIndex]?.classList.contains("an-Slide--countdown") ? "countdown" : "announcement";
      const autoplayText = isAutoplay ? " (auto-advanced)" : "";
      announcementElement.textContent = `Slide ${
        slideIndex + 1
      } of ${totalSlides}: ${slideType}${autoplayText}`;
    }

    function clearAutoplayTimeout() {
      if (autoplayTimeout) {
        clearTimeout(autoplayTimeout);
        autoplayTimeout = null;
      }
    }

    function scheduleNextAutoplay() {
      clearAutoplayTimeout();
      if (slides.length <= 1 || !autoPlay) return;
      
      // Get the current slide's custom delay if it exists, otherwise use default
      const currentSlide = slides[current];
      const customDelay = currentSlide?.dataset.autoplayDelay;
      const delay = customDelay ? parseInt(customDelay, 10) : defaultAutoplayDelay;
      
      autoplayTimeout = setTimeout(() => {
        nextSlide(false); // false = not user interaction
        // Note: scheduleNextAutoplay will be called after transition completes in onTransitionEnd
      }, delay);
    }

    function startInteractionDelay() {
      // Clear any existing delay
      if (userInteractionDelay) {
        clearTimeout(userInteractionDelay);
      }

      // Stop autoplay immediately when user interacts
      clearAutoplayTimeout();

      // Clear any pending user interactions from queue
      clearUserInteractionsFromQueue();

      // Set delay to restart autoplay only after 10 seconds of no interaction
      userInteractionDelay = setTimeout(() => {
        if (autoPlay && slides.length > 1) {
          scheduleNextAutoplay();
        }
        userInteractionDelay = null;
      }, INTERACTION_DELAY_MS);
    }

    function processNavigationQueue() {
      if (isProcessingQueue || navigationQueue.length === 0 || isTransitioning)
        return;

      isProcessingQueue = true;
      const { direction, isUserInteraction } = navigationQueue.shift();

      if (direction === "next") {
        actualPosition++;
        const next = (current + 1) % totalSlides;
        showSlide(next, "next", isUserInteraction);
      } else if (direction === "prev") {
        actualPosition--;
        const prev = (current - 1 + totalSlides) % totalSlides;
        showSlide(prev, "prev", isUserInteraction);
      }
    }

    function clearNavigationQueue() {
      navigationQueue = [];
      isProcessingQueue = false;
    }

    function clearUserInteractionsFromQueue() {
      // Remove only user interactions, keep autoplay actions
      navigationQueue = navigationQueue.filter(
        (item) => !item.isUserInteraction
      );
    }

    function showSlide(idx, direction = "next", isUserInteraction = false) {
      if (isTransitioning || idx === current) return;

      isTransitioning = true;
      const translateX = -(actualPosition * 100);
      // ensure transition is enabled; caller sets transition timing elsewhere
      slidesContainer.style.transform = `translateX(${translateX}%)`;

      indicators.forEach((btn, i) => {
        btn.setAttribute("aria-selected", i === idx ? "true" : "false");
        // Don't auto-focus indicators as it triggers focusin and pauses autoplay
      });
      slides.forEach((slide, i) => {
        slide.setAttribute("aria-hidden", i === idx ? "false" : "true");
      });

      // Wait for transitionend rather than a fixed timeout
      let called = false;
      const cleanup = () => {
        if (called) return;
        called = true;
        slidesContainer.removeEventListener("transitionend", onTransitionEnd);
        clearTimeout(fallback);
      };

      function onTransitionEnd(e) {
        if (e.target !== slidesContainer || e.propertyName !== "transform") return;
        cleanup();

        current = idx;
        isTransitioning = false;
        isProcessingQueue = false;
        updateArrowColors();
        announceSlideChange(idx, totalSlides, !isUserInteraction);
        if (typeof slider.__setActiveSlide === "function") {
          try { slider.__setActiveSlide(current); } catch (e) { /* ignore */ }
        }

        // Schedule next autoplay if this was an autoplay transition (not user interaction)
        if (!isUserInteraction && autoPlay && slides.length > 1 && !userInteractionDelay) {
          scheduleNextAutoplay();
        }

        // Handle infinite loop "jump" without transition
        if (actualPosition === 0) {
          actualPosition = totalSlides;
          slidesContainer.style.transition = "none";
          slidesContainer.style.transform = `translateX(${-actualPosition * 100}%)`;
          // force reflow to apply the transform immediately
          void slidesContainer.offsetHeight;
          // re-enable transition on next frame
          requestAnimationFrame(() => {
            slidesContainer.style.transition = "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)";
            setTimeout(() => processNavigationQueue(), 50);
          });
        } else if (actualPosition === totalSlides + 1) {
          actualPosition = 1;
          slidesContainer.style.transition = "none";
          slidesContainer.style.transform = `translateX(${-actualPosition * 100}%)`;
          void slidesContainer.offsetHeight;
          requestAnimationFrame(() => {
            slidesContainer.style.transition = "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)";
            setTimeout(() => processNavigationQueue(), 50);
          });
        } else {
          setTimeout(() => processNavigationQueue(), 50);
        }
      }

      slidesContainer.addEventListener("transitionend", onTransitionEnd);
      // fallback in case transitionend doesn't fire
      const fallback = setTimeout(() => onTransitionEnd({ target: slidesContainer, propertyName: "transform" }), 600);
    }

    function nextSlide(isUserInteraction = false) {
      if (isUserInteraction) {
        // For user interactions, clear queue and add only the latest action
        navigationQueue = [];
        navigationQueue.push({ direction: "next", isUserInteraction });
      } else {
        // For autoplay, add to queue normally
        navigationQueue.push({ direction: "next", isUserInteraction });
      }
      processNavigationQueue();
    }

    function prevSlide(isUserInteraction = false) {
      if (isUserInteraction) {
        // For user interactions, clear queue and add only the latest action
        navigationQueue = [];
        navigationQueue.push({ direction: "prev", isUserInteraction });
      } else {
        // For autoplay, add to queue normally
        navigationQueue.push({ direction: "prev", isUserInteraction });
      }
      processNavigationQueue();
    }

    function startAutoPlay() {
      // start or restart scheduling based on the active slide
      scheduleNextAutoplay();
    }

    function resetAutoPlay() {
      if (slides.length <= 1 || !autoPlay) return;
      scheduleNextAutoplay();
    }

    // listeners
    prevBtn?.addEventListener("click", () => {
      prevSlide(true); // true = user interaction
      startInteractionDelay();
    });
    nextBtn?.addEventListener("click", () => {
      nextSlide(true); // true = user interaction
      startInteractionDelay();
    });
    indicators.forEach((btn, i) => {
      btn.addEventListener("click", () => {
        const direction = i > current ? "next" : "prev";
        // treat indicator clicks as immediate user interaction:
        // clear any queued user interactions and set the actualPosition to the selected slide
        navigationQueue = [];
        actualPosition = i + 1; // account for the leading clone
        showSlide(i, direction, true); // true = user interaction
        startInteractionDelay();
      });
    });
    slider.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        prevSlide(true); // true = user interaction
        startInteractionDelay();
      }
      if (e.key === "ArrowRight") {
        nextSlide(true); // true = user interaction
        startInteractionDelay();
      }
    });

    // Pause autoplay when slider receives focus (but only from user interaction)
    slider.addEventListener("focusin", (e) => {
      // Only pause if focus came from user interaction (keyboard navigation)
      // Don't pause for programmatic focus changes
      if (e.relatedTarget !== null) {
        clearAutoplayTimeout();
      }
    });

    // Resume autoplay when slider loses focus (if autoPlay is enabled and not recently interacted)
    slider.addEventListener("focusout", () => {
      if (autoPlay && !autoplayTimeout && slides.length > 1 && !userInteractionDelay) {
        scheduleNextAutoplay();
      }
    });

    // init
    initializeCountdownTimers();
    slides.forEach((slide, i) => {
      slide.setAttribute("aria-hidden", i === 0 ? "false" : "true");
    });
    slidesContainer.style.transform = "translateX(-100%)";

    // If an accessibility helper was attached earlier, set initial active slide state
    if (typeof slider.__setActiveSlide === "function") {
      try { slider.__setActiveSlide(0); } catch (e) { /* ignore */ }
    }

    // Only hide controls if explicitly disabled or if there's only one slide
    const controlsContainer = slider.querySelector(".an-Controls");
    if (controlsContainer) {
      if (!showControls || slides.length <= 1) {
        controlsContainer.style.display = "none";
      } else {
        controlsContainer.style.display = "";
        updateArrowColors();
      }
    }

    if (autoPlay && slides.length > 1) startAutoPlay();

    window.addEventListener("beforeunload", () => {
      if (countdownInterval) clearInterval(countdownInterval);
      clearAutoplayTimeout();
      if (userInteractionDelay) clearTimeout(userInteractionDelay);
      clearNavigationQueue();
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-announcement-slider]").forEach(function (slider) {
      // initialize the full slider behavior (autoplay, queueing, listeners)
      try { initAnnouncementSlider(slider); } catch (e) { /* ignore */ }

      var slides = Array.from(slider.querySelectorAll(".an-Slide:not(.an-Slide--clone)"));
      var indicators = Array.from(slider.querySelectorAll("[data-slide-indicator]"));

      function setActive(index) {
        slides.forEach(function (s, i) {
          var isActive = i === index;
          s.setAttribute("aria-hidden", isActive ? "false" : "true");
          s.setAttribute("tabindex", isActive ? "0" : "-1");

          var content = s.querySelector(".an-Content");
          if (content) {
            if (isActive) {
              content.setAttribute("aria-live", "polite");
              content.setAttribute("aria-atomic", "true");
              // Don't programmatically move focus as it interferes with autoplay
              // Screen readers will announce changes via aria-live regions
            } else {
              content.removeAttribute("aria-live");
              content.removeAttribute("aria-atomic");
            }
          }
        });

        indicators.forEach(function (btn, i) {
          btn.setAttribute("aria-selected", i === index ? "true" : "false");
        });
      }

      // initial state
      setActive(0);

      // expose for the slider logic: call slider.__setActiveSlide(newIndex) when changing slides
      slider.__setActiveSlide = setActive;
    });
  });
})();
